import { all, call, cancel, debounce, delay, fork, put, spawn, take, takeLeading } from "typed-redux-saga";
import { Action, configureProductActions } from "./configure-product-store";
import { errorActions } from "src/components/error-viewer/error-store";
import { ConfiguratorService } from "src/backend/openapi/v1";
import { applicationActions } from "src/components/application-store";
import { claimLatestWrite, runInTurn } from "src/utils/saga-utils";

export function* getConfiguration(action: ReturnType<typeof configureProductActions.getConfigurationRequest>) {
  try {
    const result = yield* call(ConfiguratorService.getApiV1ConfiguratorConfiguration, action.payload.configurationId);
    yield put(configureProductActions.getConfigurationSuccess(result));
  } catch (error) {
    yield put(errorActions.addError(error));
    yield put(configureProductActions.getConfigurationFail());
  }
}

export function* watchGetConfiguration() {
  yield takeLeading(Action.GetConfigurationRequest, getConfiguration);
}

export function* updateProjectLineItemAttribute(
  action: ReturnType<typeof configureProductActions.updateProjectLineItemAttributeRequest>,
  isSuperseded: () => boolean = () => false
) {
  try {
    yield put(applicationActions.setGlobalLoadingBar(true));
    const result = yield* call(ConfiguratorService.putApiV1ConfiguratorSystemProductConfigurationsAttributes, action.payload.data.systemProductConfigurationItemId, action.payload.data);

    // A newer edit to this same attribute arrived while this write was queued or on the wire, so this
    // answer is already out of date. Applying it would re-seed the form from it — blanking the field the
    // user has typed into since — only for the newer response to fill it back in a moment later. The write
    // itself stands; it is the stale echo of it that is dropped.
    if (isSuperseded()) return;

    // On the edited line, this request is the authority on exactly the parts it sent, and on nothing else.
    // Two things follow. A part the response omits was deleted by this write (an empty value clears the row
    // server-side), and the reducer merges per part, so an omission would leave the old value standing —
    // spell the clear out as an empty element instead. And a part this request never touched is a
    // same-transaction read of someone else's row, stale if responses resolve out of order, so it is
    // dropped rather than applied. Both come out of the same projection below.
    const editedItemId = action.payload.data.systemProductConfigurationItemId;
    const requestedAttributeId = action.payload.data.attributeId;
    const requestedParts = [...new Set((action.payload.data.value ?? []).map(v => v.part ?? null))];

    // the backend now returns one entry per line item touched by a rule cascade off the edited attribute (possibly
    // several), each carrying only its own affected attributes rather than that item's full list — dispatch once per entry
    for (const item of result) {
      // Other lines carry a cascade result, not an echo of this write — the parts this request sent say
      // nothing about what those lines hold, so their attributes are applied as they arrive.
      const attributes =
        item.systemProductConfigurationItemId === editedItemId
          ? item.attributes.map(attribute =>
              attribute.attributeId === requestedAttributeId
                ? { ...attribute, value: requestedParts.map(part => attribute.value.find(v => v.part === part) ?? { part, value: "", valueDescription: "" }) }
                : attribute
            )
          : item.attributes;
      yield put(configureProductActions.updateProjectLineItemAttributes(item.systemProductConfigurationItemId, attributes));
    }
  } catch (error) {
    yield put(errorActions.addError(error));
    action.payload.onError?.();
  } finally {
    yield put(applicationActions.setGlobalLoadingBar(false));
  }
}

// attribute-renderer.tsx dispatches this on every keystroke for Text/Integer/Decimal/DimensionType (not
// just blur), so a plain debounce() would coalesce every field into a single shared window — typing in one
// field then quickly switching to another within the window would silently drop the first field's save,
// since redux-saga's debounce() only keeps the LATEST action of a given type. This debounces per
// systemProductConfigurationItemId+attributeId instead, so edits to different attributes each get their
// own independent window and can't cancel each other's save.
//
// Keyed per attribute, NOT per part: a DimensionType write carries every part it has (attribute-renderer.tsx),
// so cancelling a pending Width save in favour of a later Height save drops nothing — the Height action
// already holds the Width value. It also means completing both parts inside one window costs a single PUT,
// which is what keeps the backend from ever evaluating the dimension half-filled and answering isValid:false.
//
// Reference/List/Color/Bool commit once per discrete user action (a selection/click, not a keystroke) and
// dispatch with shouldDebounce: false (see attribute-renderer.tsx) — those skip the delay/cancel dance
// entirely, since there's nothing to coalesce. They still go through runInTurn: the queue is about not
// having two writes to one attribute in flight at once, which a discrete commit can cause just as well.
export function* watchUpdateProjectLineItemAttribute() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- redux-saga's Task type isn't exported from typed-redux-saga's public API; this map only tracks tasks for cancellation
  const pendingTaskByKey = new Map<string, any>();

  while (true) {
    const action = yield* take<ReturnType<typeof configureProductActions.updateProjectLineItemAttributeRequest>>(Action.UpdateProjectLineItemAttributeRequest);

    const key = `${action.payload.data.systemProductConfigurationItemId}:${action.payload.data.attributeId}`;

    // Claimed here rather than inside the spawned write: a later edit makes this one's response stale from
    // the moment the user makes it, not from whenever its own request gets its turn.
    const isSuperseded = claimLatestWrite(key);

    if (!action.payload.shouldDebounce) {
      yield* spawn(runInTurn, key, updateProjectLineItemAttribute, action, isSuperseded);
      continue;
    }

    const existingTask = pendingTaskByKey.get(key);
    if (existingTask) yield* cancel(existingTask);

    const task = yield* fork(function* () {
      yield* delay(1000);
      // Detached, so cancelling this task can only ever cancel the wait above. Past here the write is
      // queued behind whatever is still on the wire for the same key (runInTurn) — cancelling it there
      // would discard the response while the server still applied the request, which is what let two
      // saves of one attribute race each other onto the same rows.
      yield* spawn(runInTurn, key, updateProjectLineItemAttribute, action, isSuperseded);
    });
    pendingTaskByKey.set(key, task);
  }
}

export function* updateConfigurationDescription(action: ReturnType<typeof configureProductActions.updateConfigurationDescriptionRequest>) {
  try {
    yield put(applicationActions.setGlobalLoadingBar(true));
    const result = yield* call(ConfiguratorService.putApiV1ConfiguratorConfiguration, action.payload.data.systemProductConfigurationId, action.payload.data);
    yield put(configureProductActions.updateConfigurationDescriptionSuccess(result));
  } catch (error) {
    yield put(errorActions.addError(error));
  } finally {
    yield put(applicationActions.setGlobalLoadingBar(false));
  }
}

export function* watchUpdateConfigurationDescription() {
  yield debounce(500, Action.UpdateConfigurationDescriptionRequest, updateConfigurationDescription);
}

export function* generateConfigurationOrder(action: ReturnType<typeof configureProductActions.getGenerateConfigurationOrderRequest>) {
  try {
    // a refresh leaves the already-loaded parts list on screen (see the reducer), so the global bar is
    // the only signal that it is being rebuilt
    yield put(applicationActions.setGlobalLoadingBar(true));
    const result = yield* call(ConfiguratorService.getApiV1ConfiguratorGenerateConfigurationOrder, action.payload.projectId, action.payload.configurationId);
    yield put(configureProductActions.getGenerateConfigurationOrderSuccess(result));
  } catch (error) {
    yield put(errorActions.addError(error));
    yield put(configureProductActions.getGenerateConfigurationOrderFail());
  } finally {
    yield put(applicationActions.setGlobalLoadingBar(false));
  }
}

export function* watchgenerateConfigurationOrder() {
  yield debounce(1000, Action.GetGenerateConfigurationOrderRequest, generateConfigurationOrder);
}

export function* configureProductSaga() {
  yield all([
    fork(watchGetConfiguration),
    fork(watchUpdateProjectLineItemAttribute),
    fork(watchUpdateConfigurationDescription),
    fork(watchgenerateConfigurationOrder)
  ]);
}
